import { useState } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import { useTheme } from '../context/ThemeContext.jsx'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { WELLNESS_CALCS } from '../data/wellness.js'

export default function Navbar() {
  const { user, logout } = useAuth()
  const { theme, toggle } = useTheme()
  const { isAdmin } = usePeptides()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const close = () => setOpen(false)

  // Calendar & My Calculations are shown to everyone; when logged out, the page
  // itself shows a "log in to use this feature" gate (see ProtectedRoute).
  const PEPTIDE_MENU = [
    { to: '/peptides', label: 'Peptide Library' },
    { to: '/charts', label: 'Dosing Charts' },
    { to: '/peptide-dosage-chart', label: 'Dosage Chart (by vial size)' },
    { to: '/stacks', label: 'Stacks' },
    { to: '/guides', label: 'Guides' },
    { to: '/articles', label: 'Articles' },
    { to: '/glossary', label: 'Glossary' },
  ]

  const links = [
    { to: '/calculator', label: 'Calculator' },
    { to: '/peptides', label: 'Peptide Education', dropdown: PEPTIDE_MENU },
    { to: '/wellness', label: 'Wellness', dropdown: WELLNESS_CALCS.map((c) => ({ to: `/wellness/${c.slug}`, label: c.name })) },
    { to: '/calendar', label: 'Calendar' },
    { to: '/saved', label: 'My Calculations' },
    { to: '/admin', label: 'Admin', admin: true },
  ]

  const handleLogout = () => {
    logout()
    close()
    navigate('/')
  }

  const visibleLinks = links.filter((l) => !(l.admin && !isAdmin))

  return (
    <nav className="nav">
      <div className="container nav-inner">
        <Link to="/" className="brand" onClick={close}>
          <img src="/favicon.svg" className="logo" width="34" height="34" alt="CalculateMyPeptides logo" />
          <span className="brand-name">Calculate<span className="accent">My</span>Peptides</span>
        </Link>

        <div className={`nav-links ${open ? 'open' : ''}`}>
          {visibleLinks.map((l) =>
            l.dropdown ? (
              <div key={l.to} className="nav-dd">
                <NavLink to={l.to} className="nav-link" onClick={close}>
                  {l.label} <span className="nav-dd-caret">▾</span>
                </NavLink>
                <div className="nav-dd-panel">
                  {l.dropdown.map((item) => (
                    <NavLink key={item.to} to={item.to} className="nav-dd-item" onClick={close}>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink key={l.to} to={l.to} className="nav-link" onClick={close}>
                {l.label}
              </NavLink>
            )
          )}

          {/* auth actions inside the dropdown on mobile */}
          <div className="nav-mobile-auth">
            {user ? (
              <>
                <Link to="/settings" className="btn btn-ghost btn-block" onClick={close}>⚙️ Notification settings</Link>
                <button className="btn btn-ghost btn-block" onClick={handleLogout}>Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-block" onClick={close}>Log in</Link>
                <Link to="/register" className="btn btn-primary btn-block" onClick={close}>Sign up</Link>
              </>
            )}
          </div>
        </div>

        <div className="nav-spacer" />

        <div className="nav-user">
          <button className="icon-btn" onClick={toggle} title="Toggle theme" aria-label="Toggle theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          <div className="nav-auth-desktop">
            {user ? (
              <>
                <Link to="/settings" className="icon-btn" title="Notification settings" aria-label="Settings">⚙️</Link>
                <div className="avatar" title={user.email}>{user.name.slice(0, 1).toUpperCase()}</div>
                <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">Log in</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign up</Link>
              </>
            )}
          </div>

          <button className="icon-btn nav-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu" aria-expanded={open}>
            {open ? '✕' : '☰'}
          </button>
        </div>
      </div>
    </nav>
  )
}
